import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { BarChart3, CheckCircle, ChevronRight, LineChart, PieChart, TrendingUp, FileText, Brain, Sparkles, Menu, X, Play, Target, BookOpen, Calculator, ClipboardList } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LandingPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [videos, setVideos] = useState([]);
  const [promo, setPromo] = useState(null);
  const [pricing, setPricing] = useState({ price: 55, access_duration_days: 90, access_duration_months: 3 });
  const { login } = useAuth();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [videosRes, promoRes, pricingRes] = await Promise.all([
          axios.get(`${API}/videos`),
          axios.get(`${API}/promo/status`),
          axios.get(`${API}/pricing`)
        ]);
        setVideos(videosRes.data);
        setPricing(pricingRes.data);
        if (promoRes.data.is_active) {
          setPromo(promoRes.data);
        }
      } catch (err) {
        // Silently fail
      }
    };
    fetchData();
  }, []);

  const handleQuickLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please enter email and password');
      return;
    }
    setLoading(true);
    try {
      const userData = await login(email, password);
      toast.success('Welcome back!');
      if (userData.subscription_status === 'active') {
        navigate('/dashboard');
      } else {
        navigate('/payment');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const features = [
    {
      icon: <Target className="w-8 h-8 text-indigo-600" />,
      title: 'AI Research Planning',
      desc: 'Auto-generate research aims, objectives, and methodology aligned to academic standards'
    },
    {
      icon: <BookOpen className="w-8 h-8 text-indigo-600" />,
      title: 'Literature Review Outline',
      desc: 'AI-powered literature review structure with thematic sections and keyword suggestions'
    },
    {
      icon: <Calculator className="w-8 h-8 text-indigo-600" />,
      title: 'Sample Size Calculator',
      desc: "Cochran's and Yamane's formulas with confidence levels and margin of error settings"
    },
    {
      icon: <ClipboardList className="w-8 h-8 text-indigo-600" />,
      title: 'Data Collection Tools',
      desc: 'Build surveys and interview schedules with multiple question types and public sharing'
    },
    {
      icon: <BarChart3 className="w-8 h-8 text-indigo-600" />,
      title: 'Statistical Analysis',
      desc: 'Descriptive stats, regression, ANOVA, t-tests, chi-square, and non-parametric tests'
    },
    {
      icon: <Brain className="w-8 h-8 text-indigo-600" />,
      title: 'AI Interpretation',
      desc: 'Publication-ready explanations of your results powered by advanced AI models'
    }
  ];

  const quantFeatures = [
    'AI-generated research objectives',
    'Sample size calculation (Cochran & Yamane)',
    'Survey builder with 8 question types',
    'Descriptive statistics (18+ measures)',
    'Reliability analysis (Cronbach\'s Alpha)',
    'Correlation analysis (Pearson & Spearman)',
    'Simple & multiple regression',
    'T-tests (independent & paired)',
    'ANOVA (one-way & two-way)',
    'Chi-square tests',
    'Non-parametric tests'
  ];

  const qualFeatures = [
    'Literature review outline generator',
    'Interview schedule builder',
    'Manual response recording',
    'Thematic analysis',
    'Content analysis',
    'Sentiment analysis',
    'Word clouds & frequency bubbles',
    'Theme clustering',
    'Key quotation extraction'
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-indigo-700 rounded-xl flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-serif font-bold text-slate-900">StatsXY</span>
            </div>
            
            <nav className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-slate-600 hover:text-indigo-700 transition-colors">Features</a>
              <a href="#videos" className="text-slate-600 hover:text-indigo-700 transition-colors">Tutorials</a>
              <a href="#pricing" className="text-slate-600 hover:text-indigo-700 transition-colors">Pricing</a>
              <Link to="/login" className="text-slate-600 hover:text-indigo-700 transition-colors">Sign In</Link>
              <Link to="/signup">
                <Button className="btn-primary" data-testid="header-signup-btn">Get Started</Button>
              </Link>
            </nav>

            <button 
              className="md:hidden p-2" 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-slate-200 animate-slide-down">
              <nav className="flex flex-col gap-4">
                <a href="#features" className="text-slate-600 hover:text-indigo-700">Features</a>
                <a href="#videos" className="text-slate-600 hover:text-indigo-700">Tutorials</a>
                <a href="#pricing" className="text-slate-600 hover:text-indigo-700">Pricing</a>
                <Link to="/login" className="text-slate-600 hover:text-indigo-700">Sign In</Link>
                <Link to="/signup">
                  <Button className="btn-primary w-full">Get Started</Button>
                </Link>
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Promo Banner */}
      {promo && (
        <Link 
          to="/promo" 
          className="fixed top-16 left-0 right-0 z-40 block bg-gradient-to-r from-green-500 to-emerald-500 text-white py-3 text-center hover:from-green-600 hover:to-emerald-600 transition-all"
          data-testid="promo-banner"
        >
          <div className="max-w-7xl mx-auto px-4 flex items-center justify-center gap-2 text-sm sm:text-base">
            <span className="font-medium">Limited Time Offer:</span>
            <span>Get StatsXY for just ${promo.promo_price} / {pricing.access_duration_months} months (Save {Math.round((1 - promo.promo_price / pricing.price) * 100)}%)</span>
            <ChevronRight className="w-4 h-4" />
          </div>
        </Link>
      )}

      {/* Hero Section */}
      <section className={`${promo ? 'pt-32' : 'pt-24'} pb-16 lg:pt-32 lg:pb-24 relative overflow-hidden`}>
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1765046255479-669cf07a0230?crop=entropy&cs=srgb&fm=jpg&q=85')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left - Hero Content */}
            <div className="animate-slide-up">
              <p className="label-ui text-indigo-700 mb-4">Complete Research Planning & Analysis Platform</p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold text-slate-900 leading-tight mb-6">
                Plan & Analyze Your Research <span className="text-indigo-700">End-to-End</span>
              </h1>
              <p className="text-lg text-slate-600 mb-8 max-w-xl">
                From generating research objectives to literature review planning, sample size calculation, data collection, and publication-ready analysis — StatsXY guides you through your entire research journey.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/signup">
                  <Button className="btn-primary text-lg px-10 py-4" data-testid="hero-cta-btn">
                    Access Now <ChevronRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <a href="#features">
                  <Button variant="outline" className="btn-secondary text-lg px-10 py-4">
                    Learn More
                  </Button>
                </a>
              </div>
            </div>

            {/* Right - Quick Sign In Box */}
            <div className="card-marketing p-8 animate-fade-in" style={{ animationDelay: '0.2s' }}>
              <h3 className="text-2xl font-serif font-semibold text-slate-900 mb-6">Sign In</h3>
              <form onSubmit={handleQuickLogin} className="space-y-4">
                <div>
                  <Label htmlFor="quick-email" className="text-slate-700">Email</Label>
                  <Input
                    id="quick-email"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input-field mt-1"
                    data-testid="quick-login-email"
                  />
                </div>
                <div>
                  <Label htmlFor="quick-password" className="text-slate-700">Password</Label>
                  <Input
                    id="quick-password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input-field mt-1"
                    data-testid="quick-login-password"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="btn-primary w-full" 
                  disabled={loading}
                  data-testid="quick-login-submit"
                >
                  {loading ? 'Signing in...' : 'Sign In'}
                </Button>
                <div className="flex items-center justify-between text-sm">
                  <Link to="/login" className="text-indigo-700 hover:underline">Forgot password?</Link>
                  <Link to="/signup" className="text-indigo-700 hover:underline">Create account</Link>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section id="features" className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="label-ui text-indigo-700 mb-4">From Planning to Publication</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-slate-900">
              Everything You Need for Research Success
            </h2>
            <p className="text-lg text-slate-600 mt-4 max-w-2xl mx-auto">
              Plan your research methodology, collect data, run analyses, and get AI-powered interpretations — all in one platform.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, i) => (
              <div 
                key={i} 
                className="card-marketing group hover:border-indigo-200 animate-slide-up"
                style={{ animationDelay: `${i * 0.1}s` }}
                data-testid={`feature-card-${i}`}
              >
                <div className="w-14 h-14 bg-indigo-50 rounded-xl flex items-center justify-center mb-4 group-hover:bg-indigo-100 transition-colors">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-serif font-semibold text-slate-900 mb-2">{feature.title}</h3>
                <p className="text-slate-600">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* YouTube Videos Section */}
      {videos.length > 0 && (
        <section id="videos" className="py-16 lg:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <p className="label-ui text-indigo-700 mb-4">Learn How to Use StatsXY</p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-slate-900">
                Video Tutorials & Guides
              </h2>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {videos.map((video, i) => (
                <div 
                  key={video.id} 
                  className="card-marketing overflow-hidden group"
                  data-testid={`video-card-${i}`}
                >
                  <div className="relative aspect-video bg-slate-900 rounded-lg overflow-hidden mb-4">
                    <img 
                      src={`https://img.youtube.com/vi/${video.youtube_id}/maxresdefault.jpg`}
                      alt={video.title}
                      className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity"
                      onError={(e) => {
                        e.target.src = `https://img.youtube.com/vi/${video.youtube_id}/hqdefault.jpg`;
                      }}
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <a 
                        href={video.youtube_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-16 h-16 bg-indigo-600 rounded-full flex items-center justify-center hover:bg-indigo-700 transition-colors shadow-lg"
                      >
                        <Play className="w-8 h-8 text-white ml-1" />
                      </a>
                    </div>
                    <span className={`absolute top-2 right-2 px-2 py-1 rounded text-xs font-medium ${
                      video.category === 'tutorial' ? 'bg-blue-100 text-blue-700' :
                      video.category === 'marketing' ? 'bg-green-100 text-green-700' :
                      'bg-purple-100 text-purple-700'
                    }`}>
                      {video.category}
                    </span>
                  </div>
                  <h3 className="text-lg font-serif font-semibold text-slate-900 mb-2">{video.title}</h3>
                  {video.description && (
                    <p className="text-sm text-slate-600">{video.description}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Quantitative & Qualitative Sections */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Quantitative */}
            <div className="card-marketing" data-testid="quant-section">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
                  <BarChart3 className="w-6 h-6 text-indigo-700" />
                </div>
                <h3 className="text-2xl font-serif font-semibold text-slate-900">Quantitative Research</h3>
              </div>
              <p className="text-slate-600 mb-6">
                From research planning to statistical analysis — generate objectives, calculate sample sizes, build surveys, and run comprehensive tests with automatic interpretation.
              </p>
              <ul className="space-y-3">
                {quantFeatures.map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-slate-700">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Qualitative */}
            <div className="card-marketing" data-testid="qual-section">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-rose-100 rounded-xl flex items-center justify-center">
                  <FileText className="w-6 h-6 text-rose-600" />
                </div>
                <h3 className="text-2xl font-serif font-semibold text-slate-900">Qualitative Research</h3>
              </div>
              <p className="text-slate-600 mb-6">
                Plan your literature review, create interview schedules, record responses, and analyze text data with AI-powered theme extraction and sentiment analysis.
              </p>
              <ul className="space-y-3">
                {qualFeatures.map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-slate-700">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="label-ui text-indigo-700 mb-4">Simple Pricing</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-slate-900">
              One Plan. Everything Included.
            </h2>
          </div>

          <div className="max-w-md mx-auto">
            <div className="card-marketing border-2 border-indigo-200 text-center relative overflow-hidden" data-testid="pricing-card">
              {promo && (
                <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-green-500 to-emerald-500 text-white text-sm font-medium py-2">
                  Limited Time Offer - Save {Math.round((1 - promo.promo_price / pricing.price) * 100)}%!
                </div>
              )}
              <div className={`inline-block px-4 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm font-medium mb-4 ${promo ? 'mt-8' : ''}`}>
                Full Access
              </div>
              <div className="mb-6">
                {promo ? (
                  <>
                    <span className="text-2xl text-slate-400 line-through mr-2">${pricing.price}</span>
                    <span className="text-5xl font-serif font-bold text-green-600">${promo.promo_price}</span>
                    <span className="text-slate-500"> / {pricing.access_duration_months} months</span>
                  </>
                ) : (
                  <>
                    <span className="text-5xl font-serif font-bold text-slate-900">${pricing.price}</span>
                    <span className="text-slate-500"> / {pricing.access_duration_months} months</span>
                  </>
                )}
              </div>
              <ul className="space-y-3 mb-8 text-left">
                {[
                  'All quantitative analyses',
                  'All qualitative analyses',
                  'Cronbach\'s Alpha reliability',
                  'AI-powered interpretations',
                  'Publication-ready exports',
                  'Secure data storage',
                  `${pricing.access_duration_months} months full access`
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-slate-700">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link to={promo ? "/promo" : "/signup"}>
                <Button className="btn-primary w-full text-lg py-4" data-testid="pricing-cta-btn">
                  {promo ? `Start Now – $${promo.promo_price}` : `Start Now – $${pricing.price}`}
                </Button>
              </Link>
              <p className="text-sm text-slate-500 mt-4">Secure payment via PayPal</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-slate-900 text-slate-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-indigo-600 rounded-xl flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-serif font-bold text-white">StatsXY</span>
            </div>
            <p className="text-slate-400 text-sm">
              © {new Date().getFullYear()} StatsXY. AI-Powered Statistics & Research Analytics.
            </p>
            <div className="flex gap-6 text-sm">
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
              <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
